import { useCallback, useEffect, useState } from 'react';
import { teamEntriesService, type TeamEntriesPage, type TeamEntry } from '../../services/team-entries';
import type { CompetitionRound } from '../../types/competition-round';

export interface TeamEntriesState {
  page: TeamEntriesPage | null;
  loading: boolean;
  loadFailed: boolean;
  reload: () => Promise<void>;
}

/**
 * A team manager's squads at one competition, read once for the page: the
 * squads' cards (TeamEntriesSection) list them and write to them, and each
 * round's card unfolds the ones it runs before it starts.
 *
 * Read only when `enabled` — somebody named on a squad here, which is nearly
 * nobody — so everybody else's visit makes no request.
 */
export function useTeamEntries(competitionSlug: string | undefined, enabled: boolean): TeamEntriesState {
  const [page, setPage] = useState<TeamEntriesPage | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadFailed, setLoadFailed] = useState(false);

  // The failure goes to state, not to a toast: a callback closing over `t` or
  // `toast` changes identity on every render and would refetch in a loop.
  const reload = useCallback(async () => {
    if (!competitionSlug || !enabled) return;
    try {
      setPage(await teamEntriesService.list(competitionSlug));
      setLoadFailed(false);
    } catch {
      setLoadFailed(true);
    } finally {
      setLoading(false);
    }
  }, [competitionSlug, enabled]);

  useEffect(() => {
    reload();
  }, [reload]);

  return { page, loading, loadFailed, reload };
}

/**
 * The squad's athletes who climb this round: sorted into one of its classes,
 * and — on a turn — booked on it or on none yet, since an entry with no turn
 * booked is on every turn's board until it chooses (CompetitionParticipant.climbing_in).
 */
export function squadInRound(
  entries: TeamEntry[],
  round: Pick<CompetitionRound, 'id' | 'category_ids' | 'bookable'>
): TeamEntry[] {
  return entries.filter(
    (entry) =>
      !!entry.category_id &&
      round.category_ids.includes(entry.category_id) &&
      (!round.bookable || !entry.competition_round_id || entry.competition_round_id === round.id)
  );
}
